import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { 
  INDUSTRY_DESCRIPTIONS
} from '../data/industryDefaults';

interface IndustryDropdownProps {
  value: string;
  onChange: (value: string) => void;
}

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  placement: 'bottom' | 'top';
}

export const IndustryDropdown: React.FC<IndustryDropdownProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({ 
    top: 0, 
    left: 0, 
    width: 0,
    maxHeight: 400,
    placement: 'bottom'
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>();

  // Calculate optimal dropdown position
  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = 400; // Approximate max height
    const spacing = 8; // Space between button and dropdown

    // Calculate available space above and below
    const spaceBelow = viewportHeight - rect.bottom - spacing;
    const spaceAbove = rect.top - spacing;

    // Determine placement (prefer bottom, flip to top if not enough space)
    const placement: 'bottom' | 'top' = spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove ? 'bottom' : 'top';

    // Calculate position based on placement
    let top: number;
    let maxHeight: number;

    if (placement === 'bottom') {
      top = rect.bottom + spacing;
      // Limit height to available space, leaving some margin
      maxHeight = Math.min(400, spaceBelow - 20);
    } else {
      // Calculate height first to position correctly above
      maxHeight = Math.min(400, spaceAbove - 20);
      top = rect.top - maxHeight - spacing;
    }

    // Ensure dropdown doesn't go off screen horizontally
    let left = rect.left;
    const dropdownWidth = rect.width;
    
    // Adjust if dropdown would overflow right edge
    if (left + dropdownWidth > viewportWidth - 10) {
      left = viewportWidth - dropdownWidth - 10;
    }
    
    // Adjust if dropdown would overflow left edge
    if (left < 10) {
      left = 10;
    }

    setDropdownPosition({
      top: Math.max(10, top), // Ensure minimum distance from top
      left,
      width: rect.width,
      maxHeight,
      placement
    });
  }, []);

  // Update position on scroll, resize, or when dropdown opens
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      // Use requestAnimationFrame for smooth updates
      animationFrameRef.current = requestAnimationFrame(() => {
        calculatePosition();
      });
    };

    // Initial position calculation
    calculatePosition();

    // Add event listeners for position updates
    window.addEventListener('scroll', updatePosition, true); // Capture phase to catch all scroll events
    window.addEventListener('resize', updatePosition);
    
    // Also listen to potential parent container scrolls
    let scrollableParent = buttonRef.current?.parentElement;
    const scrollListeners: Array<[Element, () => void]> = [];
    
    while (scrollableParent && scrollableParent !== document.body) {
      if (scrollableParent.scrollHeight > scrollableParent.clientHeight ||
          scrollableParent.scrollWidth > scrollableParent.clientWidth) {
        scrollableParent.addEventListener('scroll', updatePosition);
        scrollListeners.push([scrollableParent, updatePosition]);
      }
      scrollableParent = scrollableParent.parentElement;
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      scrollListeners.forEach(([element, handler]) => {
        element.removeEventListener('scroll', handler);
      });
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isOpen, calculatePosition]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
        buttonRef.current?.focus(); // Return focus to button
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const filteredIndustries = INDUSTRY_DESCRIPTIONS.filter(industry => 
    industry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    industry.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (industryName: string) => {
    onChange(industryName);
    setIsOpen(false);
    setSearchTerm('');
    buttonRef.current?.focus(); // Return focus to button after selection
  };

  const selectedIndustry = INDUSTRY_DESCRIPTIONS.find(i => i.name === value);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select industry"
        className="w-full p-3 text-left border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-white flex items-center justify-between"
      >
        <div className="flex-1">
          {value ? (
            <div>
              <div className="font-medium">{value}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {selectedIndustry?.description}
              </div>
            </div>
          ) : (
            <span className="text-gray-500">Type or scroll and select your industry...</span>
          )}
        </div>
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && ReactDOM.createPortal(
        <div 
          ref={dropdownRef}
          role="listbox"
          aria-label="Industry options"
          className={`fixed bg-white border border-gray-300 rounded-lg shadow-xl overflow-hidden ${
            dropdownPosition.placement === 'top' ? 'origin-bottom' : 'origin-top'
          }`}
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            maxHeight: `${dropdownPosition.maxHeight}px`,
            zIndex: 9999,
            opacity: dropdownPosition.top === 0 ? 0 : 1, // Hide until positioned
          }}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 bg-white sticky top-0 z-10">
            <input
              type="text"
              placeholder="Search industries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              autoFocus
              aria-label="Search industries"
            />
          </div>
          
          {/* Industry Options */}
          <div className="overflow-y-auto" style={{ maxHeight: `${dropdownPosition.maxHeight - 60}px` }}>
            {filteredIndustries.map(industry => {
              return (
                <button
                  key={industry.name}
                  type="button"
                  role="option"
                  aria-selected={value === industry.name}
                  onClick={() => handleSelect(industry.name)}
                  className={`w-full text-left p-3 hover:bg-indigo-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                    value === industry.name ? 'bg-indigo-50' : ''
                  }`}
                >
                  <div className="font-medium text-gray-900">{industry.name}</div>
                  <div className="text-xs text-gray-600 mt-0.5">{industry.description}</div>
                </button>
              );
            })}
            {filteredIndustries.length === 0 && (
              <div className="p-4 text-sm text-gray-500 text-center">
                No industries found matching "{searchTerm}"
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};